export type AutosaveState = 'idle' | 'saving' | 'saved' | 'error'

/**
 * "Opslaan… / Opgeslagen / Niet opgeslagen – opnieuw proberen" (IA-13, ED-52).
 *
 * The status region is always there, empty while idle: screen readers only read a live
 * region reliably when it exists before its text changes, so keep this component
 * mounted rather than rendering it per state. A failure is an alert of its own (IA-14),
 * which is read out as it appears.
 */
export function AutosaveIndicator({
  state,
  message,
  onRetry,
}: {
  state: AutosaveState
  /** Why the save failed (the Dutch API message, design §4.12); only shown with state "error". */
  message?: string | null
  onRetry?: () => void
}) {
  // The API's sentence ends in a full stop, which would sit in front of the dash.
  const reason = state === 'error' ? message?.trim().replace(/\.+$/, '') : undefined
  return (
    // Running text, not a flex row: a long reason wraps like a sentence and keeps
    // "opnieuw proberen" in one piece.
    <span data-slot="autosave-indicator" className="text-xs break-words">
      <span role="status" className="text-muted-foreground">
        {state === 'saving' ? 'Opslaan…' : state === 'saved' ? 'Opgeslagen' : null}
      </span>
      {state === 'error' ? (
        <span role="alert" className="text-destructive">
          Niet opgeslagen
          {reason ? `: ${reason}` : null}
          {onRetry ? (
            <>
              {' – '}
              <button
                type="button"
                className="whitespace-nowrap underline underline-offset-2"
                onClick={onRetry}
              >
                opnieuw proberen
              </button>
            </>
          ) : null}
        </span>
      ) : null}
    </span>
  )
}
